/**
 * useMonitoring
 *
 * Responsabilidade: Calcular uso real da organização em relação aos limites do plano.
 * Usa `fetchAvailablePlans` para obter campos estruturados (maxContacts, maxWhatsapp, etc.)
 * em vez de parsear strings de feature — Single Responsibility + DRY.
 */

import { useState, useEffect, useMemo } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { fetchAvailablePlans } from '../../../services/modules/subscription/subscription.api';
import type { SubscriptionPlan } from '../../../domain/entities/Subscription';

/** -1 = ilimitado no banco → Infinity no frontend */
const toLimit = (value: number): number => (value === -1 ? Infinity : value);

export const useMonitoring = () => {
    const {
        leads,
        contacts,
        subscription,
        whatsappConnections,
        metaAccounts,
        isLoading: crmLoading
    } = useCrmData();

    const [fullPlans, setFullPlans] = useState<SubscriptionPlan[]>([]);
    const [plansLoading, setPlansLoading] = useState(true);

    // Busca os planos completos (com maxContacts, maxWhatsapp, etc.)
    useEffect(() => {
        fetchAvailablePlans()
            .then(setFullPlans)
            .catch((e) => console.error('[useMonitoring] Erro ao buscar planos:', e))
            .finally(() => setPlansLoading(false));
    }, []);

    const usageData = useMemo(() => {
        if (!subscription) return null;

        // Encontra o plano pelo nome (match flexível: planId ou name)
        const currentPlan = fullPlans.find(
            (p) => p.id === subscription.planName || p.name === subscription.planName
        );

        const contactLimit = currentPlan ? toLimit(currentPlan.maxContacts) : Infinity;
        const whatsAppLimit = currentPlan ? toLimit(currentPlan.maxWhatsapp) : Infinity;
        const adAccountLimit = currentPlan ? toLimit(currentPlan.maxAdAccounts) : Infinity;
        const funnelLimit = currentPlan ? toLimit(currentPlan.maxFunnels) : Infinity;

        return {
            planName: currentPlan?.name ?? subscription.planName,
            userUsage: {
                used: subscription.usedSeats,
                total: subscription.totalSeats,
            },
            contactUsage: {
                used: contacts.length,
                total: contactLimit,
            },
            leadUsage: {
                used: leads.length,
                total: Infinity, // Leads não têm limite direto de contagem — é cobrado por excedente
            },
            whatsAppUsage: {
                used: whatsappConnections.length,
                total: whatsAppLimit,
            },
            adAccountUsage: {
                used: metaAccounts.length,
                total: adAccountLimit,
            },
            funnelUsage: {
                used: 0, // Funis são exibidos em outra tela
                total: funnelLimit,
            },
            totalLeadsExceeded: 0,
        };
    }, [subscription, fullPlans, leads, contacts, whatsappConnections, metaAccounts]);

    return {
        usageData,
        isLoading: crmLoading || plansLoading,
    };
};
